"use client";

import { CircleCheck as CheckCircle2, DraftingCompass, Layers, Wrench, ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function AdditionDecisions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  const structuralDecisions = [{
    title: "Room type",
    description: "Determining whether you are adding a ground-floor primary suite, expansive family room, sunroom, or second-story bump-out."
  }, {
    title: "Size and square footage",
    description: "Balancing usable living area with zoning setbacks, yard footprint, and optimal traffic flow from the existing floor plan."
  }, {
    title: "Foundation tie-in",
    description: "Evaluating your existing sub-structure to select an engineered slab extension, crawlspace stem wall, or excavated basement foundation."
  }, {
    title: "Roofline design",
    description: "Deciding between gable, hip, or shed tie-ins to align pitches, shed water away from intersections, and match the original architecture."
  }];
  const costDrivers = [{
    title: "Foundation work",
    description: "Excavation depth, soil bearing capacity, retaining walls, and footings required to anchor the new perimeter safely."
  }, {
    title: "Roofline tie-in",
    description: "Integrating new trusses with existing rafters, framing valleys, and re-flashing intersections for weatherproofing."
  }, {
    title: "Square footage added",
    description: "The total volume of framing lumber, drywall, roofing material, insulation, and interior trim required for the footprint."
  }, {
    title: "Structural complexity",
    description: "Removing exterior load-bearing walls, installing steel support beams, and re-routing primary electrical and plumbing lines."
  }, {
    title: "Permitting scope",
    description: "Municipal site plan approvals, architectural stamps, structural engineering calculations, and phased inspector sign-offs."
  }];
  return <section id="homeadditiondecisions" data-nav="light" ref={ref} className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className={`max-w-3xl mb-14 lg:mb-18 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
        opacity: isVisible ? 1 : 0
      }}>
          <div className="flex items-center gap-2 text-primary font-medium text-sm mb-3">
            <DraftingCompass className="size-4" />
            <span>Addition Planning Guide</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold tracking-tight text-foreground">
            Key layout choices and cost factors for your home addition
          </h2>
          <p className="mt-4 text-base sm:text-lg text-foreground/80 leading-relaxed">
            Expanding your home requires clear structural decisions early in design. Review the core architectural choices that dictate layout alongside the technical variables that drive project investment.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-stretch">
          {/* List 1: Structural Decisions */}
          <Card className={`border border-border/80 bg-card shadow-sm flex flex-col justify-between transition-all duration-700 delay-100 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
          opacity: isVisible ? 1 : 0
        }}>
            <div>
              <div className="relative aspect-16/9 w-full overflow-hidden rounded-t-xl bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/home_addition_framing_tie_in.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/home_addition_framing_tie_in.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/home_addition_framing_tie_in.png-wvc-sizes" alt="Timber framing and roofline tie-in during a residential home addition project" loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 hover:scale-102" />
              </div>

              <CardHeader className="pt-6 pb-2">
                <div className="flex items-center gap-2.5 text-secondary">
                  <div className="p-2 rounded-md bg-secondary/10 text-secondary">
                    <Layers className="size-5" />
                  </div>
                  <CardTitle className="text-2xl font-serif font-semibold text-foreground">
                    Layout & structural decisions
                  </CardTitle>
                </div>
                <p className="text-sm text-foreground/75 mt-1">
                  Architectural choices you will finalize during the concept and drafting phases.
                </p>
              </CardHeader>

              <Separator className="my-4 mx-6 w-auto" />

              <CardContent className="pt-0">
                <ul className="space-y-4">
                  {structuralDecisions.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                      <div className="mt-1 shrink-0 text-primary">
                        <CheckCircle2 className="size-4.5" />
                      </div>
                      <div>
                        <span className="font-semibold text-foreground block text-base">
                          {item.title}
                        </span>
                        <span className="text-sm text-foreground/75 leading-relaxed block mt-0.5">
                          {item.description}
                        </span>
                      </div>
                    </li>)}
                </ul>
              </CardContent>
            </div>
          </Card>

          {/* List 2: Cost-Driving Factors */}
          <Card className={`border border-border/80 bg-card shadow-sm flex flex-col justify-between transition-all duration-700 delay-200 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
          opacity: isVisible ? 1 : 0
        }}>
            <div>
              <div className="relative aspect-16/9 w-full overflow-hidden rounded-t-xl bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/finished_home_addition_exterior.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/finished_home_addition_exterior.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/finished_home_addition_exterior.png-wvc-sizes" alt="Seamlessly integrated exterior addition with matching rooflines and foundation masonry" loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 hover:scale-102" />
              </div>

              <CardHeader className="pt-6 pb-2">
                <div className="flex items-center gap-2.5 text-secondary">
                  <div className="p-2 rounded-md bg-secondary/10 text-secondary">
                    <Wrench className="size-5" />
                  </div>
                  <CardTitle className="text-2xl font-serif font-semibold text-foreground">
                    Cost-driving factors
                  </CardTitle>
                </div>
                <p className="text-sm text-foreground/75 mt-1">
                  Structural and site variables that influence overall construction scope.
                </p>
              </CardHeader>

              <Separator className="my-4 mx-6 w-auto" />

              <CardContent className="pt-0">
                <ul className="space-y-4">
                  {costDrivers.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                      <div className="mt-1 shrink-0 text-primary">
                        <CheckCircle2 className="size-4.5" />
                      </div>
                      <div>
                        <span className="font-semibold text-foreground block text-base">
                          {item.title}
                        </span>
                        <span className="text-sm text-foreground/75 leading-relaxed block mt-0.5">
                          {item.description}
                        </span>
                      </div>
                    </li>)}
                </ul>
              </CardContent>
            </div>
          </Card>
        </div>
      </div>
    </section>;
}